'use client';

import Link from 'next/link';
import { motion, Variants } from 'framer-motion';
import Image from 'next/image';

const HeroClient = () => {
  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const textVariants: Variants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
      },
    },
  };

  const imageVariants: Variants = {
    hidden: { opacity: 0, scale: 1.1 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 1.2,
        ease: 'easeOut',
      },
    },
  };

  const foodImages = [
    {
      src: '/assets/product_images/hero_sub_1b.png',
      alt: 'Delicious Haitian food',
      delay: 0.2,
    },
    {
      src: '/assets/product_images/hero_sub_2.png',
      alt: 'Authentic Haitian cuisine',
      delay: 0.4,
    },
    {
      src: '/assets/product_images/hero_sub_3.png',
      alt: 'Traditional Haitian dish',
      delay: 0.6,
    },
  ];

  return (
    <div className="relative bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center pt-10">
          {/* Left Side - Text & CTA */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-8 text-center md:text-left ">
            {/* Main Headlines */}
            <motion.div variants={textVariants} className="space-y-4">
              <h1 className="text-3xl md:text-7xl font-bold text-gray-800 leading-tight tracking-tight">
                Made to Be{' '}
                <span className="bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
                  Remembered
                </span>
              </h1>
              <p className="text-2xl md:text-3xl text-gray-600 font-light">Freshly prepared & delivered with love.</p>
            </motion.div>

            {/* Subheadline */}
            <motion.p
              variants={textVariants}
              className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-lg font-light">
              From savory patties to full event spreads, Jayouz Cuisine brings warmth to your table.
            </motion.p>
            {/* CTA Buttons */}
            <motion.div variants={textVariants} className="flex flex-col sm:flex-row gap-4 pt-4">
              <motion.div whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.98 }}>
                <Link
                  href="/menu"
                  className="group inline-flex items-center gap-3 bg-gradient-to-r from-orange-600 to-orange-500 text-white px-8 py-4 rounded-3xl text-lg font-semibold hover:from-orange-500 hover:to-amber-500 transition-all duration-300 shadow-lg hover:shadow-xl">
                  Our Menu
                  <motion.div
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="ml-1">
                    →
                  </motion.div>
                </Link>
              </motion.div>

              <motion.div whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.98 }}>
                <Link
                  href="/contact"
                  className="group inline-flex items-center gap-3 border-2 border-orange-600 text-orange-600 px-8 py-4 rounded-3xl text-lg font-semibold hover:from-orange-500 hover:to-amber-500 transition-all duration-300 shadow-lg hover:shadow-xl">
                  Contact Us
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Right Side - Image Collage */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="relative lg:order-last order-first">
            {/* Main hero image */}
            <motion.div
              variants={imageVariants}
              className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[16/10]">
              <div className="relative w-full h-full ">
                <Image
                  src="/assets/product_images/hero.png"
                  alt="Beautifully plated homemade Haitian meal"
                  fill
                  sizes="(max-width: 768px) 100vw, 50vw"
                  className="object-cover"
                  priority
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            </motion.div>

            {/* Food collage - staggered animations */}
            <div className="grid grid-cols-3 gap-4 mt-6">
              {foodImages.map((image, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8, y: 30 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{
                    duration: 0.6,
                    delay: image.delay,
                    ease: 'easeOut',
                  }}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="relative rounded-2xl overflow-hidden shadow-lg cursor-pointer aspect-square">
                  <div className="relative w-full h-full">
                    <Image
                      src={image.src}
                      alt={image.alt}
                      fill
                      sizes="(max-width: 768px) 33vw, 25vw"
                      className="object-cover"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent" />
                </motion.div>
              ))}
            </div>

            {/* Decorative elements */}
            <motion.div
              animate={{
                rotate: [0, 360],
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: 'linear',
              }}
              className="absolute -top-6 -right-6 w-16 h-16 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full opacity-20 blur-sm"
            />

            <motion.div
              animate={{
                y: [0, -10, 0],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              className="absolute -bottom-4 -left-4 w-20 h-20 bg-gradient-to-br from-orange-400 to-amber-500 rounded-full opacity-30 blur-lg"
            />
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default HeroClient;
